import moment from "moment";
import { clock, dayHours } from "@1-now/booking-core";

/** One booked window on a day, as the calendar's `partial_unavailable` gives it ("HH:mm" pairs). */
export type DayWindow = { from: string; to: string };

export type DayState = "booked" | "partial" | "past" | "open";

const dayKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/**
 * 1NOW-11176: what a calendar day looks like for THIS car, from the answers the picker already gets
 * (full_unavailable, partial_unavailable): `booked` (the car is out all day: struck through and greyed),
 * `partial` (out for part of it: a marker, and its free hours on tap or hover), `past` (before today: greyed,
 * not struck), `open`. Nothing here asks the API anything or decides what can be booked: the picker's own
 * selectable rule still does that.
 */
export const dayState = (
  date: Date,
  fullyUnavailable: Date[],
  partial: Record<string, DayWindow[]>,
  today: Date = new Date(),
): DayState => {
  if (fullyUnavailable.some((d) => d.toDateString() === date.toDateString())) return "booked";
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (date < start) return "past";
  return partial[dayKey(date)]?.length ? "partial" : "open";
};

export const partialKey = dayKey;

const minutes = (hhmm: string) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm);
  return m ? Number(m[1]) * 60 + Number(m[2]) : 0;
};
const hhmm = (mins: number) =>
  `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

/** The calendar writes "to the end of the day" as 23:59. */
const END_OF_DAY = 23 * 60 + 59;

/**
 * The hours of a partly available day the car IS free, from the windows it is out: "free until 3 PM",
 * "free from 11 AM", "free until 9 AM and from 5 PM", "free 1 PM – 3 PM". Null when it is out all day
 * (the day would be `booked`, not `partial`).
 */
export const freeHoursLabel = (windows: DayWindow[] | undefined, company?: any): string | null => {
  if (!windows?.length) return null;
  const out = windows
    .map((w) => [minutes(w.from), Math.min(minutes(w.to), END_OF_DAY)] as [number, number])
    .sort((a, b) => a[0] - b[0]);
  // The free stretches are what is left of the day between the windows.
  const free: [number, number][] = [];
  let cursor = 0;
  for (const [from, to] of out) {
    if (from > cursor) free.push([cursor, from]);
    cursor = Math.max(cursor, to);
  }
  if (cursor < END_OF_DAY) free.push([cursor, END_OF_DAY]);
  if (!free.length) return null;
  const parts = free.map(([from, to]) => {
    if (from === 0) return `until ${clock(hhmm(to), company)}`;
    if (to >= END_OF_DAY) return `from ${clock(hhmm(from), company)}`;
    return `${clock(hhmm(from), company)} – ${clock(hhmm(to), company)}`;
  });
  return `free ${parts.join(" and ")}`;
};

/**
 * A branch's hours on a day, in the operator's clock: "6:00 AM – 10:00 PM", "Open 24 hours", "Closed Sunday",
 * or null when the branch (and the company behind it) states no hours. Without a date it reads today.
 */
export const branchHoursText = (branch: any, date: Date | null | undefined, company: any): string | null => {
  const hours = dayHours(branch, date ?? new Date(), company);
  if (hours.closed) return hours.weekday ? `Closed ${hours.weekday}` : "Closed";
  if (hours.allDay) return "Open 24 hours";
  if (hours.from && hours.to) return `${clock(hours.from, company)} – ${clock(hours.to, company)}`;
  return null;
};

/** A trip's dates in little room: "Oct 9–11" within a month, "Oct 30 – Nov 2" across months, "Oct 9" for one day. */
export const compactRange = (start: Date, end: Date): string => {
  const a = moment(start);
  const b = moment(end);
  if (a.isSame(b, "day")) return a.format("MMM D");
  if (a.isSame(b, "month")) return `${a.format("MMM D")}–${b.format("D")}`;
  return `${a.format("MMM D")} – ${b.format("MMM D")}`;
};
